// Snowman collision and finish-state checks.
import type { PlayerPos, RockPos, ShowGameOverFn, TreePos } from './index.js';
import { getTestModeValues } from '../test-mode.js';

// Finish line: the run ends when the player crosses this z (skiing in -Z). This is the
// SINGLE SOURCE OF TRUTH for the finish trigger — the live run ends here, and course.ts
// derives its FINISH_Z (HUD / gates / ghost) from it so the displayed finish and the
// enforced finish can't drift. The winnability/forward-stress gates read it via course.ts.
export const FINISH_Z = -195;

/** One airborne obstacle clear this frame (jump-system completion JP-2, #245):
 *  the horizontal-overlap test WOULD have collided, but an airborne suppression
 *  branch (isJumpingHighAboveTrees / isJumpingOverRock) let the player sail over.
 *  `key` identifies the obstacle (stable per run) so the caller can dedup the
 *  many consecutive overlap frames of one pass into a single scored clear. */
export interface ObstacleClear {
  type: 'tree' | 'rock';
  key: string;
}

interface SnowmanCollisionState {
  pos: PlayerPos;
  isInAir: boolean;
  terrainHeightAtPosition: number;
  treePositions: TreePos[];
  rockPositions: RockPos[];
  gameActive: boolean;
  showGameOver: ShowGameOverFn;
  // Invoked with this frame's airborne clears BEFORE the finish/game-over check
  // below can fire showGameOver synchronously — so a clear banked on the finish
  // frame still lands on the result screen (same rationale as bankAirScore, #186).
  // Detection only: provenance / dedup / cap / scoring are the caller's policy.
  onObstaclesCleared?: (clears: ObstacleClear[]) => void;
}

export function detectCollisionsAndFinish(state: SnowmanCollisionState): void {
  const {
    pos,
    isInAir,
    terrainHeightAtPosition,
    treePositions,
    rockPositions,
    gameActive,
    showGameOver,
    onObstaclesCleared
  } = state;

  const testModes = getTestModeValues(window.location.search);
  const isTestMode = testModes.length > 0;
  const isVerboseTestMode = testModes.includes('true');

  // Airborne clears observed this frame (JP-2). Collected inside the collision
  // walks below — a clear is exactly "overlap true AND suppression branch fired".
  const clears: ObstacleClear[] = [];

  // Check if snowman is off the terrain or falling
  const fallThreshold = 0.5; // How far below terrain to allow before reset

  // Check for tree collisions
  // Use the window variable for collision radius if set (for testing), otherwise use default
  const treeCollisionRadius = window.treeCollisionRadius || 2.5; // Collision distance for trees

  // In test mode, output complete tree positions for debugging
  if (isVerboseTestMode && treePositions.length > 0) {
    console.log(`TREES LOADED: ${treePositions.length} trees found`);
    console.log(`SNOWMAN POS: x=${pos.x.toFixed(2)}, y=${pos.y.toFixed(2)}, z=${pos.z.toFixed(2)}`);
    console.log(`FIRST TREE: x=${treePositions[0]!.x.toFixed(2)}, y=${treePositions[0]!.y.toFixed(2)}, z=${treePositions[0]!.z.toFixed(2)}`);
  }

  // Count trees in extended terrain area for logging
  const extendedTreesCount = treePositions.filter(t => t.z < -80).length;
  const totalTreesCount = treePositions.length;

  // Log tree information when in test mode
  if (isVerboseTestMode && !window._treeCheckLogged) {
    console.log(`TREE COLLISION INFO: ${totalTreesCount} total trees, ${extendedTreesCount} in extended area (z < -80)`);

    // Log the ranges to verify coverage
    if (treePositions.length > 0) {
      const zMin = Math.min(...treePositions.map(t => t.z));
      const zMax = Math.max(...treePositions.map(t => t.z));
      const xMin = Math.min(...treePositions.map(t => t.x));
      const xMax = Math.max(...treePositions.map(t => t.x));
      console.log(`TREE COLLISION RANGES: X: ${xMin.toFixed(1)} to ${xMax.toFixed(1)}, Z: ${zMin.toFixed(1)} to ${zMax.toFixed(1)}`);
    }
    window._treeCheckLogged = true;
  }

  // Check collision with any tree
  const treeCollision = treePositions.some((treePos, treeIndex) => {
    // Special case for tests - direct position match or very close positions always collide
    // Use a small epsilon for floating point comparison, increased for test reliability
    // This helps with floating-point precision issues in tests
    const epsilon = isTestMode ? 0.1 : 0.001;

    const exactMatch =
      Math.abs(pos.x - treePos.x) < epsilon &&
      Math.abs(pos.z - treePos.z) < epsilon;

    // Check horizontal distance for collision (2D distance ignoring height)
    const dx = pos.x - treePos.x;
    const dz = pos.z - treePos.z;
    const horizontalDistance = Math.sqrt(dx*dx + dz*dz);

    // We only detect collision if the horizontal distance is close enough
    // Tree collision only happens when snowman is on the ground or close to it
    const isCloseEnough = horizontalDistance < treeCollisionRadius;

    // Clearance is height-based only: once the snowman is airborne and high enough
    // above the tree base it clears the tree whether it is still rising or already
    // descending past the jump apex. This mirrors the rock clearance below and fixes
    // the "sail over a tree and land clear of the trunk" case on black/expert lines:
    // requiring upward motion (verticalVelocity > 0) crashed every descending-but-high
    // pass — you could only clear a tree on the way UP, so any jump whose downward arc
    // still overlapped the trunk radius (i.e. most real jumps over a tree) crashed even
    // though the snowman was well above the tree. (Same bug already fixed for rocks.)
    const isJumpingHighAboveTrees = isInAir && pos.y > (treePos.y + 5);

    // The exact-center match must respect the SAME airborne clearance as the radius
    // path (#398): it used to return a hit unconditionally, so a jump arcing directly
    // over a trunk center crashed even while the snowman was well above the tree —
    // the one horizontal line the height-based clearance above didn't cover. An
    // exact-center pass that the clearance suppresses is still a scored clear (JP-2);
    // the radius branch below would push the same key, but `return false` here exits
    // this tree's walk before it runs, so the clear is recorded exactly once.
    if (exactMatch) {
      if (isJumpingHighAboveTrees) {
        clears.push({ type: 'tree', key: `t${treeIndex}` });
        return false;
      }
      if (isVerboseTestMode) {
        console.log(`DIRECT TREE HIT at (${pos.x.toFixed(2)}, ${pos.z.toFixed(2)})`);
      }
      return true;
    }

    // Airborne clear (JP-2): this frame WOULD have hit the tree but the suppression
    // branch is letting the player sail over — record it (index keys the obstacle for
    // per-air-phase dedup by the caller). Pure observation: collision outcome unchanged.
    if (isCloseEnough && isJumpingHighAboveTrees) {
      clears.push({ type: 'tree', key: `t${treeIndex}` });
    }

    // Debug collision in browser tests when needed
    if (isVerboseTestMode) {
      // For extended terrain trees, log additional info
      const inExtendedArea = treePos.z < -80;
      console.log(`TREE CHECK: dist=${horizontalDistance.toFixed(2)}, radius=${treeCollisionRadius}, jumping=${isJumpingHighAboveTrees}, collision=${isCloseEnough && !isJumpingHighAboveTrees}, extended=${inExtendedArea}`);

      // Extra debugging info for very close trees
      if (horizontalDistance < 5) {
        console.log(`CLOSE TREE: x=${treePos.x.toFixed(2)}, y=${treePos.y.toFixed(2)}, z=${treePos.z.toFixed(2)}, snowman: x=${pos.x.toFixed(2)}, y=${pos.y.toFixed(2)}, z=${pos.z.toFixed(2)}`);
      }
    }

    // Special handling for tree jumping test
    if (window.testTreeJumpingCheck && isJumpingHighAboveTrees) {
      console.log(`TREE JUMPING TEST: Allowing jump over tree (dist=${horizontalDistance.toFixed(2)})`);
      // Don't detect collision during jump - normal game behavior
      return false;
    }

    // Allow jumping over trees but collide when on the ground
    return isCloseEnough && !isJumpingHighAboveTrees;
  });

  // Check collision with large, exposed rocks. Small half-buried stones remain
  // terrain detail; only positions returned by Mountains.addRocks reach this list.
  const rockCollision = rockPositions.some((rockPos, rockIndex) => {
    const dx = pos.x - rockPos.x;
    const dz = pos.z - rockPos.z;
    const horizontalDistance = Math.sqrt(dx*dx + dz*dz);
    // Collision radius (max 3u). Kept in sync with Mountains.rockCollisionRadius,
    // which the placement-time safe-zone uses to exclude rocks that would reach the
    // ski lane/spawn pocket. Duplicated inline (rather than imported) to keep the
    // snowman/collision layer independent of mountain placement helpers during R3.
    const rockRadius = Math.max(1.25, Math.min(3.0, rockPos.size * 0.75 + 0.75));
    // Height-aware clearance (#348): every hazard from mountains/rocks.ts carries
    // the world-space top of its actual placed mesh, so "jumping over" requires
    // clearing what the player SEES — a tall crag or pinch gate can no longer be
    // sailed through at the old flat 0.7·size height. The legacy model remains as
    // the fallback for synthetic fixtures without topY.
    const exposedRockTop = rockPos.topY ?? (rockPos.y + rockPos.size * 0.7);
    // Clearance is height-based: once the snowman is airborne and above the rock
    // top it clears the hazard whether it is still rising or already descending past
    // the jump apex. (Requiring upward motion made descending-but-high jumps crash.)
    const isJumpingOverRock = isInAir && pos.y > exposedRockTop + 0.5;

    if (isVerboseTestMode && horizontalDistance < 5) {
      console.log(`ROCK CHECK: dist=${horizontalDistance.toFixed(2)}, radius=${rockRadius.toFixed(2)}, jumping=${isJumpingOverRock}, collision=${horizontalDistance < rockRadius && !isJumpingOverRock}`);
    }

    // Airborne clear (JP-2): overlapping the rock while the height-based clearance
    // suppresses the hit. Mirrors the tree branch above; observation only.
    if (horizontalDistance < rockRadius && isJumpingOverRock) {
      clears.push({ type: 'rock', key: `r${rockIndex}` });
    }

    return horizontalDistance < rockRadius && !isJumpingOverRock;
  });

  // Surface this frame's airborne clears BEFORE the finish/game-over block below —
  // showGameOver can fire synchronously (finish line) and build the result screen,
  // so a clear scored on that same frame must already be banked (JP-2).
  if (clears.length && onObstaclesCleared) onObstaclesCleared(clears);

  // Reset if: reaches end of slope, goes off sides, falls off terrain, or hits a tree
  // Allow wider boundaries to match the extended mountain terrain
  // Only skip boundary check during regression/tree tests, but NOT during browser tests or unified tests
  const inExtendedMountainTest = testModes.some((mode) =>
    mode === 'regression' || mode === 'tree' || mode === 'trees'
  ); // Only skip for specific tests
  if (pos.z < FINISH_Z || // finish line (single source of truth; course.ts derives from this)
      (!inExtendedMountainTest && Math.abs(pos.x) > 120) || // Keep boundary check during browser/unified tests
      (!isInAir && pos.y < terrainHeightAtPosition - fallThreshold) ||
      treeCollision ||
      rockCollision) {

    if (gameActive) {
      // Determine the reason for game over
      let reason = "You crashed!";

      if (treeCollision) {
        reason = "BANG!!! You hit a tree!";
      } else if (rockCollision) {
        reason = "BANG!!! You hit a rock!";
      } else if (pos.z < FINISH_Z) {
        reason = "You reached the end of the slope!";
      } else if (Math.abs(pos.x) > 120) {
        reason = "You went off the mountain!";
      } else if (!isInAir && pos.y < terrainHeightAtPosition - fallThreshold) {
        reason = "You fell off the terrain!";
      }

      showGameOver(reason);
    }
  }
}
